import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Star, MapPin, ArrowRight, Loader2 } from 'lucide-react';
import { useFeaturedVendors } from '@/hooks/useVendor';
import { VENDOR_CATEGORIES, PRICE_RANGE_LABELS } from '@/types/vendor';

const FeaturedVendors = () => {
  const { data: featuredVendors = [], isLoading } = useFeaturedVendors(6);

  if (isLoading) {
    return (
      <section className="py-20 bg-romantic">
        <div className="container mx-auto px-4 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </section>
    );
  }

  if (featuredVendors.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-romantic">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
          <div>
            <span className="text-primary font-medium mb-2 block">Selección especial</span>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-foreground mb-4">
              Proveedores Destacados
            </h2>
            <p className="text-muted-foreground max-w-xl">
              Los mejores profesionales elegidos por nuestro equipo y valorados por cientos de parejas.
            </p>
          </div>
          <Link to="/vendors" className="mt-4 md:mt-0">
            <Button variant="outline" className="gap-2">
              Ver todos
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredVendors.map((vendor) => {
            const category = VENDOR_CATEGORIES.find(c => c.id === vendor.category);
            
            return (
              <Link
                key={vendor.id}
                to={`/vendor/${vendor.slug}`}
                className="group"
              >
                <article className="bg-card rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-elevated hover:-translate-y-1 border border-border">
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={vendor.image || 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=800'}
                      alt={vendor.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4 flex gap-2">
                      <span className="bg-card/90 backdrop-blur-sm text-foreground text-xs font-medium px-3 py-1 rounded-full">
                        {category?.icon} {category?.name}
                      </span>
                    </div>
                    <div className="absolute top-4 right-4">
                      <span className="bg-accent text-accent-foreground text-xs font-bold px-3 py-1 rounded-full">
                        {PRICE_RANGE_LABELS[vendor.priceRange]}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-serif text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                        {vendor.name}
                      </h3>
                      <div className="flex items-center gap-1 text-accent">
                        <Star className="w-4 h-4 fill-current" />
                        <span className="font-semibold text-sm">{vendor.rating.toFixed(1)}</span>
                        <span className="text-muted-foreground text-xs">({vendor.reviewCount})</span>
                      </div>
                    </div>
                    
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                      {vendor.shortDescription || vendor.description}
                    </p>
                    
                    <div className="flex items-center text-muted-foreground text-sm">
                      <MapPin className="w-4 h-4 mr-1" />
                      {vendor.location || 'España'}
                    </div>
                  </div>
                </article>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedVendors;
